// Web 应用数据备份和恢复服务（使用 localStorage）
import type { AppData } from '@ext-bookmark/shared/types';

const _STORAGE_KEY = 'bookmark_navigator_data';
  void _STORAGE_KEY;
const BACKUP_KEY_PREFIX = 'bookmark_navigator_data_backup_';
const MAX_BACKUPS = 10; // 最多保留10个备份

interface BackupData {
  data: AppData;
  timestamp: number;
  createdAt: string;
}

/**
 * 创建数据备份
 */
export async function createBackup(data: AppData): Promise<string> {
  const timestamp = Date.now();
  const backupKey = `${BACKUP_KEY_PREFIX}${timestamp}`;
  
  try {
    const backup: BackupData = {
      data,
      timestamp,
      createdAt: new Date().toISOString(),
    };
    
    localStorage.setItem(backupKey, JSON.stringify(backup));
    
    // 清理旧备份，只保留最新的 MAX_BACKUPS 个
    await cleanupOldBackups();
    
    return backupKey;
  } catch (error) {
    console.error('[Backup] 创建备份失败:', error);
    throw error;
  }
}

/**
 * 获取所有备份列表
 */
export async function listBackups(): Promise<Array<{ key: string; timestamp: number; createdAt: string }>> {
  try {
    const backups: Array<{ key: string; timestamp: number; createdAt: string }> = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(BACKUP_KEY_PREFIX)) {
        try {
          const backupStr = localStorage.getItem(key);
          if (backupStr) {
            const backup: BackupData = JSON.parse(backupStr);
            if (backup && backup.timestamp && backup.createdAt) {
              backups.push({
                key,
                timestamp: backup.timestamp,
                createdAt: backup.createdAt,
              });
            }
          }
        } catch (e) {
          // 忽略解析错误
          console.debug('[Backup] 解析备份失败:', key, e);
        }
      }
    }
    
    // 按时间戳降序排序（最新的在前）
    backups.sort((a, b) => b.timestamp - a.timestamp);
    return backups;
  } catch (error) {
    console.error('[Backup] 获取备份列表失败:', error);
    return [];
  }
}

/**
 * 恢复备份数据
 */
export async function restoreBackup(backupKey: string): Promise<AppData | null> {
  try {
    const backupStr = localStorage.getItem(backupKey);
    if (!backupStr) {
      return null;
    }
    
    const backup: BackupData = JSON.parse(backupStr);
    if (!backup || !backup.data) {
      return null;
    }
    
    return backup.data;
  } catch (error) {
    console.error('[Backup] 恢复备份失败:', error);
    return null;
  }
}

/**
 * 删除备份
 */
export async function deleteBackup(backupKey: string): Promise<void> {
  try {
    localStorage.removeItem(backupKey);
  } catch (error) {
    console.error('[Backup] 删除备份失败:', error);
    throw error;
  }
}

/**
 * 清理旧备份，只保留最新的 MAX_BACKUPS 个
 */
async function cleanupOldBackups(): Promise<void> {
  try {
    const backups = await listBackups();
    
    if (backups.length > MAX_BACKUPS) {
      const toDelete = backups.slice(MAX_BACKUPS);
      for (const backup of toDelete) {
        await deleteBackup(backup.key);
      }
    }
  } catch (error) {
    console.error('[Backup] 清理旧备份失败:', error);
    // 不抛出错误，清理失败不影响主流程
  }
}

/**
 * 导出数据为 JSON 字符串（用于手动备份）
 */
export function exportDataAsJSON(data: AppData): string {
  return JSON.stringify(data, null, 2);
}

/**
 * 从 JSON 字符串导入数据（用于手动恢复）
 */
export function importDataFromJSON(jsonString: string): AppData | null {
  try {
    return JSON.parse(jsonString) as AppData;
  } catch (error) {
    console.error('[Backup] 导入数据失败:', error);
    return null;
  }
}

/**
 * 获取最新的备份
 */
export async function getLatestBackup(): Promise<{ key: string; data: AppData } | null> {
  const backups = await listBackups();
  if (backups.length === 0) {
    return null;
  }
  
  const latestBackup = backups[0];
  const data = await restoreBackup(latestBackup.key);
  
  if (!data) {
    return null;
  }
  
  return {
    key: latestBackup.key,
    data,
  };
}
